import { ApiClient } from '@api/api-configuration';
import { BillingTransactionTypeEnum } from '@api/READ_ONLY/billing_api/Api';
import { BILLING_QUERY_KEYS } from '@app/common/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Text from '@app/components/ui/Text/Text';
import { Modal, Spin } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isStaffPayoutType, staffTypeFromPayoutType } from '../../Payments/staffTypes';
import { type PaymentFormValues, type PaymentPayload, type StaffType } from '../../Payments/types';
import ConductPaymentForm from '../ConductPaymentForm/ConductPaymentForm';

type EditablePayment = {
  paymentIdentifier: string;
  paymentType: BillingTransactionTypeEnum;
  staffType?: StaffType;
  visitId?: number;
  date: string;
  amount: number;
  notes?: string;
};

type UpdateConductedPaymentModalProps = {
  paymentId: number | null;
  entityIdentifier: number | null;
  onClose: () => void;
  /** Called after a successful save, before the modal closes. */
  onSaved?: () => void;
};

/** Only a client payment or a staff payout is editable here; any other type throws instead of reopening as one. */
export const toEditablePaymentType = (
  transactionType: BillingTransactionTypeEnum
): { paymentType: BillingTransactionTypeEnum; staffType?: StaffType } => {
  if (transactionType === BillingTransactionTypeEnum.ClientPayment) {
    return { paymentType: transactionType };
  }
  if (isStaffPayoutType(transactionType)) {
    return { paymentType: transactionType, staffType: staffTypeFromPayoutType(transactionType) };
  }
  throw new Error(`Transaction type ${transactionType} cannot be edited as a payment`);
};

export const fetchPaymentById = async (paymentId: number): Promise<EditablePayment | null> => {
  const response = await ApiClient.billingApi.billing.getTransactionById(paymentId);
  const transaction = response.data;

  return {
    paymentIdentifier: transaction.transactionIdentifier,
    ...toEditablePaymentType(transaction.transactionType),
    visitId: undefined,
    date: transaction.transactionDate,
    amount: transaction.amount,
    notes: transaction.notes ?? undefined,
  };
};

const UpdateConductedPaymentModal = ({
  paymentId,
  entityIdentifier,
  onClose,
  onSaved,
}: UpdateConductedPaymentModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isOpen = Boolean(paymentId);

  const paymentQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_DETAILS, paymentId],
    enabled: Boolean(paymentId),
    queryFn: () => fetchPaymentById(paymentId as number),
  });

  const updatePaymentMutation = useMutation({
    mutationFn: async (payload: PaymentPayload) => {
      if (!paymentId) return;

      await ApiClient.billingApi.billing.updateTransaction(paymentId, {
        transactionDate: payload.date,
        amount: payload.amount,
        notes: payload.notes ?? '',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLING_QUERY_KEYS.RECENT_PAYMENTS_SIDEBAR] });
      onSaved?.();
      onClose();
    },
  });

  const initialValues: PaymentFormValues = useMemo(
    () =>
      paymentQuery.data
        ? {
            paymentType: paymentQuery.data.paymentType,
            clientId:
              paymentQuery.data.paymentType === BillingTransactionTypeEnum.ClientPayment
                ? (entityIdentifier ?? undefined)
                : undefined,
            visitId:
              paymentQuery.data.paymentType === BillingTransactionTypeEnum.ClientPayment
                ? paymentQuery.data.visitId
                : undefined,
            staffType: paymentQuery.data.staffType,
            staffId:
              paymentQuery.data.paymentType !== BillingTransactionTypeEnum.ClientPayment
                ? (entityIdentifier ?? undefined)
                : undefined,
            date: dayjs(paymentQuery.data.date),
            amount: Math.abs(paymentQuery.data.amount),
            notes: paymentQuery.data.notes ?? '',
          }
        : {
            paymentType: BillingTransactionTypeEnum.ClientPayment,
            clientId: undefined,
            visitId: undefined,
            staffType: undefined,
            staffId: undefined,
            date: dayjs(),
            amount: undefined,
            notes: '',
          },
    [entityIdentifier, paymentQuery.data]
  );

  const handleSubmit = async (payload: PaymentPayload) => {
    await updatePaymentMutation.mutateAsync(payload);
  };

  return (
    <Modal open={isOpen} onCancel={onClose} footer={null} width={760} destroyOnHidden title={t('payment')}>
      {paymentQuery.isLoading ? (
        <div className="flex min-h-[240px] items-center justify-center">
          <Spin size="large" />
        </div>
      ) : paymentQuery.isError || !paymentQuery.data ? (
        <div className="flex min-h-[240px] items-center justify-center">
          <Text type="body2" transKey="payment-load-error" color="#ef4444" />
        </div>
      ) : (
        <ConductPaymentForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          isSubmitting={updatePaymentMutation.isPending}
          isClientDisabled={true}
          isPaymentTypeDisabled={true}
          isVisitDisabled={paymentQuery.data?.paymentType === BillingTransactionTypeEnum.ClientPayment}
          isStaffTypeDisabled={true}
          isStaffDisabled={true}
          submitLabelKey="update-payment"
        />
      )}
    </Modal>
  );
};

export default UpdateConductedPaymentModal;
